import type { ReplaceFileUpload } from "@/DataImports/flow/types";
import type {
  MappingResponse,
  MappingRow,
  PreviewItem
} from "@/DataImports/mappingRows";

export type RowState = "matched" | "attention" | "not_imported";
export type MapTabKey = "all" | "attention" | "matched" | "not_imported";

export interface MapRow extends MappingRow {
  preview?: PreviewItem;
  state: RowState;
}

export interface MapOption {
  value: string;
  label: string;
  help?: string;
}

// The slice of `GET /imports/:batch_type/:id.json` the Map page reads.
export interface MapImport {
  name: string;
  batch_type: string;
  record_type: string;
  vendor: string;
  rows: number | null;
  status: string;
  can?: { edit_mapping?: boolean; replace_file?: boolean };
  replace_file?: ReplaceFileUpload | null;
  files?: { file_name: string }[];
  links: {
    review_page: string;
    mapping?: string;
    start_load?: string;
    reset_mapping?: string;
    replace_file?: string;
  };
}

export const MAP_TABS: { key: MapTabKey; label: string }[] = [
  { key: "all", label: "All" },
  { key: "attention", label: "Needs attention" },
  { key: "matched", label: "Matched" },
  { key: "not_imported", label: "Not imported" }
];

export const STATE_PILLS: Record<RowState, { label: string; class: string }> = {
  matched: { label: "Matched", class: "bg-green-50 text-green-700" },
  attention: { label: "Check value", class: "bg-amber-50 text-amber-700" },
  not_imported: { label: "Not imported", class: "bg-gray-100 text-gray-600" }
};

export function withState(row: MappingRow, preview?: PreviewItem): MapRow {
  if (!row.fieldKey) return { ...row, state: "not_imported" };
  const flagged = preview?.status === "warning" || preview?.status === "error";
  return { ...row, preview, state: flagged ? "attention" : "matched" };
}

export function tabCounts(rows: MapRow[]): Record<MapTabKey, number> {
  const count = (state: RowState) =>
    rows.filter((row) => row.state === state).length;
  return {
    all: rows.length,
    attention: count("attention"),
    matched: count("matched"),
    not_imported: count("not_imported")
  };
}

export function filterRows(rows: MapRow[], tab: MapTabKey): MapRow[] {
  return tab === "all" ? rows : rows.filter((row) => row.state === tab);
}

// null (first values) -> 0 -> ... -> count - 1 -> null
export function nextSampleIndex(
  index: number | null,
  count: number
): number | null {
  if (count < 1) return null;
  if (index === null) return 0;
  return index + 1 < count ? index + 1 : null;
}

export function sampleLabel(
  sample: { index: number | null; count: number } | undefined
): string {
  if (!sample || sample.index === null) return "First values";
  return `Row ${sample.index + 1} of ${sample.count}`;
}

export function displayValue(value: unknown): string {
  return value === null || value === undefined || value === ""
    ? "--"
    : String(value);
}

type Blockers = Partial<
  Pick<MappingResponse, "errors" | "missing_required" | "blocking_rules">
>;

// `missing_required` carries field keys and `blocking_rules` carries rule messages.
export function blockerMessages(
  blockers: Blockers,
  registry: MappingResponse["registry"]
): string[] {
  const labelOf = (key: string) =>
    registry.fields.find((field) => field.key === key)?.label ?? key;
  return [
    ...(blockers.errors ?? []),
    ...(blockers.missing_required ?? []).map(
      (key) => `${labelOf(key)} is required. Choose the column that holds it.`
    ),
    ...(blockers.blocking_rules ?? [])
  ];
}

export const NO_ROWS_MESSAGE = "The file has no rows to import.";

// `sample.count` is the saved data rows; unknown (older payloads, no samples) is not "empty".
export function hasNoRows(
  mapping: Pick<MappingResponse, "sample" | "sample_rows">
): boolean {
  return (mapping.sample?.count ?? mapping.sample_rows?.length) === 0;
}

export function responseMessage(error: any, fallback: string): string {
  const data = error?.response?.data;
  return data?.errors?.join(" ") || data?.message || fallback;
}
